import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import ApparelFilters from '../ApparelFilters';
import './index.css';

const ApparelFilterDrawer = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  menuFilterSettings,
  showGenderFilter,
  availableBrands,
  availableColors,
  availableAgeGroups,
  availableSizes,
  availableCategories,
  totalProducts,
  isLoading
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const drawerContentRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // .filter-drawer-content is a NATIVE scrolling element (unlike the custom
  // thumb/track used elsewhere in the Restaurant Menu App), since the
  // ApparelFilters sidebar it wraps gives up its own internal scroll here
  // (see .filter-drawer-content .apparel-filters-sidebar in index.css) —
  // the drawer itself is what scrolls. Its scrollbar color already reveals
  // on :hover/:focus-within for desktop, but neither fires on a touchscreen,
  // so on mobile it was never visible even while scrolling. This toggles
  // the same reveal via a class instead, driven by real scroll activity,
  // and auto-hides again after a short idle period — same pattern as the
  // custom thumbs elsewhere in this app.
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = drawerContentRef.current;
    if (!el) return undefined;
    let hideTimer;

    const handleScroll = () => {
      el.classList.add('is-scrolling');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => el.classList.remove('is-scrolling'), 1000);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      clearTimeout(hideTimer);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300); // match transition duration
  };

  // Same reset shape as ApparelFilters' own clearAllFilters — duplicated
  // rather than shared so this component doesn't need a ref into its
  // child just to trigger the identical, already-simple reset. Rendered
  // here instead of relying on ApparelFilters' internal Clear All button
  // so it can sit in this header row (see JSX below) rather than its own
  // near-empty row underneath.
  const clearAllFilters = () => {
    setFilters({
      categories: [],
      priceRange: null,
      brands: [],
      colors: [],
      sizes: [],
      gender: [],
      ageGroups: [],
      minRating: null
    });
  };

  if (!isOpen && !isClosing) return null;

  return (
    <>
      <div
        className={`filter-drawer-overlay ${isClosing ? 'closing' : ''}`}
        onClick={handleClose}
      />
      <div className={`filter-drawer-container ${isClosing ? 'closing' : ''}`}>
        <div className="filter-drawer-header">
          <h3>Filters & Sorting</h3>
          <div className="filter-drawer-header-actions">
            <button className="filter-drawer-clear-all" onClick={clearAllFilters}>Clear All</button>
            <button className="filter-drawer-close" onClick={handleClose}>
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="filter-drawer-content" ref={drawerContentRef}>
          <ApparelFilters
            filters={filters}
            setFilters={setFilters}
            menuFilterSettings={menuFilterSettings}
            showGenderFilter={showGenderFilter}
            availableBrands={availableBrands}
            availableColors={availableColors}
            availableAgeGroups={availableAgeGroups}
            availableSizes={availableSizes}
            availableCategories={availableCategories}
            isLoading={isLoading}
          />
        </div>
        
        <div className="filter-drawer-footer">
          <button className="filter-drawer-apply-btn" onClick={handleClose}>
            Show {totalProducts} Products
          </button>
        </div>
      </div>
    </>
  );
};

export default ApparelFilterDrawer;
